import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Gem, Users, Lightbulb, Rocket } from "lucide-react";
import Spotlight from "../components/Spotlight";
import SectionTransition from "../components/SectionTransition";

const values = [
  { icon: Lightbulb, title: "Innovation", desc: "We push boundaries and embrace emerging technologies to deliver forward-thinking solutions." },
  { icon: Gem, title: "Quality", desc: "Every line of code, every pixel, every interaction is crafted with precision and care." },
  { icon: Users, title: "Collaboration", desc: "We work as one team with our clients, turning their vision into shared milestones." },
  { icon: Rocket, title: "Impact", desc: "We measure success by the tangible value we create for businesses and communities." },
];

const timeline = [
  { year: "Founded", title: "The Beginning", desc: "wecodXtech was born with a mission to democratize access to quality software solutions." },
  { year: "Growth", title: "Expanding Horizons", desc: "Launched our product line and expanded services to include digital marketing and e-commerce." },
  { year: "Community", title: "Nurturing Talent", desc: "Started the internship program, training students in 15+ technology domains." },
  { year: "Today", title: "Leading Innovation", desc: "Delivering enterprise solutions globally while continuing to invest in the next generation of tech talent." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <Spotlight className="bg-neutral-950 grid-pattern pt-32 pb-20 md:pt-44 md:pb-28">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-medium tracking-wider uppercase text-blue-400 mb-4 animate-fade-in-up">About Us</p>
            <h1
              data-testid="about-heading"
              className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-white mb-6 animate-fade-in-up animation-delay-200"
            >
              Who Are We
            </h1>
            <p className="text-lg md:text-xl text-slate-400 leading-relaxed animate-fade-in-up animation-delay-400">
              A team of passionate technologists, designers, and strategists united by 
              one goal: building exceptional software that transforms businesses.
            </p>
          </div>
        </div>
      </Spotlight>

      {/* Mission / Vision */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <SectionTransition>
              <div className="p-10 rounded-2xl border border-slate-200 h-full">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-6">
                  <Target size={24} className="text-blue-600" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">Our Mission</h3>
                <p className="text-slate-600 leading-relaxed">
                  To empower businesses with innovative, scalable, and reliable technology solutions
                  while nurturing the next generation of tech professionals through hands-on education 
                  and real-world experience. We believe technology should be an enabler, not a barrier.
                </p>
              </div>
            </SectionTransition>
            <SectionTransition delay={0.15}>
              <div className="p-10 rounded-2xl border border-slate-200 h-full">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-6">
                  <Eye size={24} className="text-blue-600" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">Our Vision</h3>
                <p className="text-slate-600 leading-relaxed">
                  To become a global leader in software product development and digital transformation,
                  recognized for our commitment to quality, innovation, and the development of talented 
                  professionals who shape the future of technology.
                </p>
              </div>
            </SectionTransition>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-slate-50 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionTransition>
            <p className="text-sm font-medium tracking-wider uppercase text-blue-600 mb-4">Core Values</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-16">
              What Drives Us
            </h2>
          </SectionTransition>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <SectionTransition key={v.title} delay={i * 0.1}>
                <div data-testid={`value-card-${i}`} className="p-8 rounded-2xl bg-white border border-slate-200">
                  <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center mb-5">
                    <v.icon size={22} className="text-blue-600" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                </div>
              </SectionTransition>
            ))}
          </div>
        </div>
      </section>

      {/* Team Image */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <SectionTransition>
              <div className="rounded-2xl overflow-hidden border border-slate-200">
                <img
                  src="https://images.unsplash.com/photo-1765561667528-28e39c6174dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Team collaborating"
                  className="w-full h-[350px] md:h-[450px] object-cover"
                  loading="lazy"
                />
              </div>
            </SectionTransition>
            <SectionTransition delay={0.2}>
              <p className="text-sm font-medium tracking-wider uppercase text-blue-600 mb-4">Our Story</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-6">
                Built by Engineers, for Everyone
              </h2>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-6">
                wecodXtech started with a simple belief: great software should be accessible to 
                every business, regardless of size. We combine deep technical expertise with a 
                genuine passion for problem-solving.
              </p>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                Today, we serve clients across multiple industries, delivering custom products, 
                digital services, and mentoring hundreds of students through our internship programs.
                Our team brings together diverse perspectives and skills to create solutions that matter.
              </p>
            </SectionTransition>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-slate-50 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionTransition>
            <p className="text-sm font-medium tracking-wider uppercase text-blue-600 mb-4">Journey</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-16">
              Our Path
            </h2>
          </SectionTransition>

          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-slate-200 md:-translate-x-px" />

            <div className="space-y-12">
              {timeline.map((item, i) => (
                <SectionTransition key={item.year} delay={i * 0.15}>
                  <div className={`relative flex items-start gap-8 md:gap-0 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                    {/* Dot */}
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-blue-500 border-4 border-white md:-translate-x-1.5 mt-2 z-10" />

                    {/* Content */}
                    <div className={`ml-12 md:ml-0 md:w-1/2 ${i % 2 === 0 ? "md:pr-16 md:text-right" : "md:pl-16"}`}>
                      <span className="text-sm font-semibold text-blue-600">{item.year}</span>
                      <h3 className="text-xl font-semibold text-slate-900 mt-1 mb-2">{item.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </SectionTransition>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-neutral-950 py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <SectionTransition>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6">
              Let Us Build Together
            </h2>
            <p className="text-base md:text-lg text-slate-400 max-w-xl mx-auto mb-10">
              Whether you need a product, a service, or want to join our team, 
              we would love to hear from you.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-btn"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-black font-semibold text-sm hover:bg-slate-200 transition-all duration-200 active:scale-95"
            >
              Get in Touch <ArrowRight size={16} />
            </Link>
          </SectionTransition>
        </div>
      </section>
    </div>
  );
}
